"use client";

/**
 * The hero follow hook: one tap turns an anonymous residence-page
 * visitor into a reachable device (web push, no account). The 2026-07-19
 * retention audit found ~800 people/month landing on these pages with a
 * 75% single-page bounce and NOTHING to leave behind - this button is
 * the capture.
 *
 * States: idle -> busy (spinner, label stays) -> following (the same
 * indigo button with a check: a toggle that is on). Where push cannot
 * carry the follow (iOS Safari tab, refused permission) a signed-out
 * visitor goes to the Telegram bot
 * instead (one tap on Start, still no account; 2026-10-14, after three
 * weeks in which the sign-in sheet converted nobody), a signed-in one to
 * a saved search on the residence; the account alert form via
 * `fallbackHref` stays the last resort. The second anonymous push
 * follow is refused server-side (``account_required``) and routes to
 * login with a return path - the ladder, not a dead end.
 */
import { useState } from "react";
import Link from "@/lib/localized-link";
import { useLocale, useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { openPinLimit } from "@/lib/use-pin-limit";
import { trackEvent } from "@/lib/tracking";
import { Bell, Check, Loader2, Send } from "lucide-react";
import { telegramBotHref } from "@/lib/telegram";
import {
  followViaAlert,
  goToResidenceLogin,
  type ResidenceFollowHook,
} from "./useResidenceFollow";
import { BUTTON_SECONDARY } from "@/lib/ui-classes";
import { pluralKey } from "@/lib/residence-top";

// The primary action near the top of the page (search arrivals bounced
// 63% on residence pages, 2026-10-07). Owner, 2026-11-01: the old promise
// card read empty. It now says what a follower would have been sent in
// the last 30 days (the daily run's own rules, summary.follow_preview):
// the new listings, the price drops and the newest drop with its amount,
// then where the news arrives, the follow and a filtered alert beside it.
// Nothing recent: one line with the button.
// "No account needed" is only true for someone signed out: a signed-in
// visitor gets the channels alone (owner, 2026-10-16).
type Copy = {
  short: string; following: string; follow: string; followingBtn: string; alert: string;
  window: string; newN: Record<"one" | "few" | "many", string>; dropsN: Record<"one" | "few" | "many", string>;
  lastDrop: string; rooms: string; m2: string; channels: string; channelsSigned: string;
};
const COPY: Record<string, Copy> = {
  az: {
    short: "Bu kompleksdə yeni elan çıxanda xəbər verək.",
    following: "İzləyirsiniz: yeni elanları və qiymət endirimlərini göndərəcəyik.",
    follow: "İzləyin",
    followingBtn: "İzlənilir",
    alert: "Filtrli bildiriş",
    window: "Son 30 gündə: {parts}",
    newN: { one: "{n} yeni elan", few: "{n} yeni elan", many: "{n} yeni elan" },
    dropsN: { one: "{n} qiymət endirimi", few: "{n} qiymət endirimi", many: "{n} qiymət endirimi" },
    lastDrop: "Son endirim: {what}{old} → {new} AZN",
    rooms: "{n} otaqlı",
    m2: "m²",
    channels: "İzləsəniz, belə xəbərləri brauzerdə və ya Telegram-da alarsınız. Hesab lazım deyil.",
    channelsSigned: "İzləsəniz, belə xəbərləri brauzerdə, Telegram-da və ya e-poçtla alarsınız.",
  },
  ru: {
    short: "Сообщим, когда в этом ЖК появится новое объявление.",
    following: "Вы следите: пришлём новые объявления и снижения цен.",
    follow: "Следить",
    followingBtn: "Вы следите",
    alert: "С фильтрами",
    window: "За 30 дней: {parts}",
    newN: { one: "{n} новое объявление", few: "{n} новых объявления", many: "{n} новых объявлений" },
    dropsN: { one: "{n} снижение цены", few: "{n} снижения цены", many: "{n} снижений цены" },
    lastDrop: "Последнее снижение: {what}{old} → {new} AZN",
    rooms: "{n}-комн.",
    m2: "м²",
    channels: "Если следить, такие новости придут в браузер или в Telegram. Аккаунт не нужен.",
    channelsSigned: "Если следить, такие новости придут в браузер, в Telegram или на e-mail.",
  },
  en: {
    short: "We will tell you when a new listing appears here.",
    following: "You are following: we will send new listings and price drops.",
    follow: "Follow",
    followingBtn: "Following",
    alert: "Alert with filters",
    window: "Last 30 days: {parts}",
    newN: { one: "{n} new listing", few: "{n} new listings", many: "{n} new listings" },
    dropsN: { one: "{n} price drop", few: "{n} price drops", many: "{n} price drops" },
    lastDrop: "Latest drop: {what}{old} → {new} AZN",
    rooms: "{n} rooms",
    m2: "m²",
    channels: "Follow and news like this reaches you in the browser or on Telegram. No account needed.",
    channelsSigned: "Follow and news like this reaches you in the browser, on Telegram or by e-mail.",
  },
};

/** summary.follow_preview (routes/trends, services/residence_follow_matcher). */
export interface FollowPreview {
  days: number;
  new_n: number;
  drops_n: number;
  last_drop: { rooms: number | null; area: number | null; old: number; new: number; pct: number; at: string } | null;
}

const NBSP = String.fromCharCode(0xa0);
/** "365 000" (en "365,000"), never broken inside. */
function num(n: number, locale: string): string {
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, locale === "en" ? "," : NBSP);
}
const put = (s: string, v: Record<string, string | number>) =>
  s.replace(/\{(\w+)\}/g, (m, k) => (k in v ? String(v[k]) : m));

/** The two lines of recent news, or null when there is none. */
export function recentLines(p: FollowPreview | null | undefined, locale: string): { head: string; drop: string | null } | null {
  if (!p || (p.new_n <= 0 && p.drops_n <= 0)) return null;
  const c = COPY[locale] ?? COPY.az;
  const parts = [
    p.new_n > 0 ? put(c.newN[pluralKey(p.new_n, locale)], { n: num(p.new_n, locale) }) : null,
    p.drops_n > 0 ? put(c.dropsN[pluralKey(p.drops_n, locale)], { n: num(p.drops_n, locale) }) : null,
  ].filter(Boolean).join(", ");
  const d = p.last_drop;
  const what = d
    ? [d.rooms ? put(c.rooms, { n: d.rooms }) : null, d.area ? `${d.area}${NBSP}${c.m2}` : null].filter(Boolean).join(", ")
    : "";
  return {
    head: put(c.window, { parts }),
    // "365 000 AZN" never breaks before its unit (DESIGN.md).
    drop: d ? put(c.lastDrop, { what: what ? `${what}, ` : "", old: num(d.old, locale), new: num(d.new, locale) }).replace(/ AZN/g, `${NBSP}AZN`) : null,
  };
}

export default function FollowResidenceButton({
  clusterId,
  hook,
  fallbackHref,
  preview,
}: {
  clusterId: string;
  hook: ResidenceFollowHook;
  /** The alert form prefilled with this residence (also the secondary action). */
  fallbackHref: string;
  /** The last 30 days as a follower would have heard them. */
  preview?: FollowPreview | null;
}) {
  const t = useT();
  const { locale } = useLocale();
  const c = COPY[locale] ?? COPY.az;
  const { user, loading: authLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const { state, eligible, follow, unfollow, markFollowing, via, followViaTelegram, forgetTelegram } = hook;
  const viaAlert = via === "alert";
  const viaTelegram = via === "telegram";
  const following = state === "following";
  const busy = state === "busy" || state === "unknown";
  // Push cannot carry it here: the primary action is the bot for a
  // signed-out visitor (the sign-in sheet stays one link away).
  const telegramPrimary = !eligible && !user && !following;

  // The ladder when push cannot carry the follow (blocked in the
  // browser, iOS Safari tab, permission refused): signed in, the follow
  // becomes a saved search on this residence (Telegram / e-mail) in one
  // tap; signed out, the sign-in sheet, which brings the visitor back
  // here with ?follow=1 so the page finishes the job. The alert form is
  // the last resort, only when the account has no verified e-mail.
  async function ladder() {
    if (!user) {
      trackEvent("residence.next.follow", { cluster_id: clusterId, step: "telegram" });
      followViaTelegram();
      return;
    }
    const res = await followViaAlert(clusterId);
    if (res.ok) {
      markFollowing();
      return;
    }
    if (res.reason === "email_not_verified" || res.reason === "limit") {
      window.location.href = fallbackHref;
      return;
    }
    setError(t("residence.follow.error"));
  }

  async function onClick() {
    setError(null);
    if (following) {
      if (viaTelegram) {
        // The follow lives in the chat; the bot's own message carries
        // the stop button.
        window.open(telegramBotHref(), "_blank", "noopener");
        return;
      }
      if (!viaAlert) await unfollow();
      return;
    }
    trackEvent("follow.residence.click", { cluster_id: clusterId, via: "hero" });
    trackEvent("residence.next.follow", { cluster_id: clusterId, step: "tap", push: eligible ? 1 : 0 });
    if (!eligible) {
      await ladder();
      return;
    }
    const res = await follow();
    trackEvent("residence.next.follow", { cluster_id: clusterId, step: res.ok ? "ok" : "fail", ...(res.ok ? {} : { reason: res.reason }) });
    if (!res.ok) {
      if (res.reason === "account_required") {
        goToResidenceLogin(clusterId, { follow: true });
        return;
      }
      await ladder();
    }
  }

  const recent = recentLines(preview, locale);
  const followButton = (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-pressed={following}
      className={`inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-70 ${recent ? "flex-1 sm:flex-none" : "shrink-0"} ${BUTTON_SECONDARY}`}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : following ? (
        <Check className="h-4 w-4" aria-hidden />
      ) : telegramPrimary ? (
        <Send className="h-4 w-4" aria-hidden />
      ) : (
        <Bell className="h-4 w-4" aria-hidden />
      )}
      {following
        ? (viaTelegram ? t("residence.follow.telegram_following") : c.followingBtn)
        : telegramPrimary ? t("residence.follow.telegram") : c.follow}
    </button>
  );

  return (
    <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-3 sm:p-3.5">
      {recent ? (
        <>
          <p className="text-sm font-semibold leading-snug text-gray-900 tabular-nums">{recent.head}</p>
          {recent.drop && <p className="mt-0.5 text-sm leading-snug text-gray-600 tabular-nums">{recent.drop}</p>}
          {/* "No account needed" only once we know the visitor is signed out:
              never to a signed-in person, not even for the moment the session
              loads. The longer line sits invisible in the same cell, so the
              card keeps one height while the session check answers. */}
          {/* A browser that cannot push gets the Telegram hint below
              instead, which says the same. */}
          {!(telegramPrimary && !error) && (
          <p className="mt-1.5 grid text-xs leading-snug text-gray-500">
            <span className="col-start-1 row-start-1">{following ? c.following : user || authLoading ? c.channelsSigned : c.channels}</span>
            <span aria-hidden className="invisible col-start-1 row-start-1">{c.channels.length > c.channelsSigned.length ? c.channels : c.channelsSigned}</span>
          </p>
          )}
          <div className="mt-2.5 flex items-center gap-2">
            {followButton}
            <Link
              href={fallbackHref}
              rel="nofollow"
              onClick={() => trackEvent("residence.next.alert", { cluster_id: clusterId })}
              className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 sm:flex-none"
            >
              {c.alert}
            </Link>
          </div>
        </>
      ) : (
        // Nothing in the last 30 days: one line and the button.
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 text-sm leading-snug text-gray-700">{following ? c.following : c.short}</p>
          {followButton}
        </div>
      )}
      {error && <p className="mt-1.5 text-xs text-rose-700" role="alert">{error}</p>}
      {viaAlert && !error && (
        <p className="mt-1.5 text-xs text-gray-500">{t("residence.follow.via_alert")}</p>
      )}
      {telegramPrimary && !error && (
        <p className="mt-1.5 text-xs text-gray-500">
          {t("residence.follow.telegram_hint")}{" "}
          <button
            type="button"
            onClick={() => openPinLimit(0, "follow")}
            className="font-medium text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900"
          >
            {t("residence.follow.account_instead")}
          </button>
        </p>
      )}
      {viaTelegram && !error && (
        <p className="mt-1.5 text-xs text-gray-500">
          {t("residence.follow.telegram_following_hint")}{" "}
          <button
            type="button"
            onClick={forgetTelegram}
            className="font-medium text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900"
          >
            {t("residence.follow.telegram_forget")}
          </button>
        </p>
      )}
    </div>
  );
}
